import { GOAL_FRAME } from '../constants';
import { getSceneAsset } from './assets';
import { projectPoint } from '../helpers';
import { Vec3 } from '../types';

const drawBackdrop = (ctx: CanvasRenderingContext2D, width: number, height: number, goalPulse: number) => {
  const stadiumBackdrop = getSceneAsset('stadiumBackdrop');
  if (stadiumBackdrop) {
    ctx.drawImage(stadiumBackdrop, 0, 0, width, height);
  } else {
    const sky = ctx.createLinearGradient(0, 0, 0, height);
    sky.addColorStop(0, '#071326');
    sky.addColorStop(0.48, '#12325f');
    sky.addColorStop(1, '#04101c');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, width, height);
  }

  const topGlow = ctx.createRadialGradient(width * 0.5, height * 0.14, 12, width * 0.5, height * 0.18, width * 0.44);
  topGlow.addColorStop(0, `rgba(167,139,250,${0.48 + goalPulse * 0.08})`);
  topGlow.addColorStop(0.42, `rgba(56,189,248,${0.16 + goalPulse * 0.06})`);
  topGlow.addColorStop(1, 'rgba(2,6,23,0)');
  ctx.fillStyle = topGlow;
  ctx.fillRect(0, 0, width, height);

  const haze = ctx.createLinearGradient(0, height * 0.56, 0, height);
  haze.addColorStop(0, 'rgba(2,6,23,0)');
  haze.addColorStop(1, 'rgba(2,6,23,0.74)');
  ctx.fillStyle = haze;
  ctx.fillRect(0, height * 0.34, width, height * 0.66);

  for (let row = 0; row < 3; row += 1) {
    const y = height * (0.35 + row * 0.055);
    for (let index = 0; index < 18; index += 1) {
      const x = width * 0.08 + index * width * 0.048 + (row % 2 ? width * 0.018 : 0);
      const glow = ctx.createRadialGradient(x, y, 0, x, y, width * 0.015);
      glow.addColorStop(0, row === 1 ? 'rgba(191,219,254,0.32)' : 'rgba(248,250,252,0.22)');
      glow.addColorStop(1, 'rgba(248,250,252,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(x - width * 0.018, y - width * 0.018, width * 0.036, width * 0.036);
    }
  }
};

const drawPitchBand = (ctx: CanvasRenderingContext2D, width: number, height: number, nearZ: number, farZ: number, colorA: string, colorB: string) => {
  const a = projectPoint({ x: -20, y: 0, z: nearZ }, width, height);
  const b = projectPoint({ x: 20, y: 0, z: nearZ }, width, height);
  const c = projectPoint({ x: 20, y: 0, z: farZ }, width, height);
  const d = projectPoint({ x: -20, y: 0, z: farZ }, width, height);

  const band = ctx.createLinearGradient(a.x, a.y, d.x, d.y);
  band.addColorStop(0, colorA);
  band.addColorStop(1, colorB);
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.lineTo(c.x, c.y);
  ctx.lineTo(d.x, d.y);
  ctx.closePath();
  ctx.fillStyle = band;
  ctx.fill();
};

const drawPitchLines = (ctx: CanvasRenderingContext2D, width: number, height: number, points: Vec3[]) => {
  ctx.beginPath();
  points.forEach((point, index) => {
    const projected = projectPoint(point, width, height);
    if (index === 0) ctx.moveTo(projected.x, projected.y);
    else ctx.lineTo(projected.x, projected.y);
  });
  const first = projectPoint(points[0], width, height);
  ctx.lineTo(first.x, first.y);
  ctx.stroke();
};

export const drawPitch = (ctx: CanvasRenderingContext2D, width: number, height: number, goalPulse: number) => {
  for (let index = 0; index < 10; index += 1) {
    const nearZ = 5.8 + ((23.6 - 5.8) * index) / 10;
    const farZ = 5.8 + ((23.6 - 5.8) * (index + 1)) / 10;
    drawPitchBand(ctx, width, height, nearZ, farZ, index % 2 === 0 ? '#0A5C52' : '#137266', index % 2 === 0 ? '#083930' : '#0f5a4f');
  }

  const turfShadow = ctx.createLinearGradient(0, height * 0.62, 0, height * 0.94);
  turfShadow.addColorStop(0, 'rgba(5,15,23,0)');
  turfShadow.addColorStop(1, 'rgba(3,10,18,0.82)');
  ctx.fillStyle = turfShadow;
  ctx.fillRect(0, height * 0.56, width, height * 0.44);

  for (let stripe = 0; stripe < 8; stripe += 1) {
    const x = width * 0.08 + stripe * width * 0.11;
    ctx.fillStyle = `rgba(255,255,255,${stripe % 2 === 0 ? 0.04 : 0.018})`;
    ctx.beginPath();
    ctx.moveTo(x, height * 0.61);
    ctx.lineTo(x + width * 0.05, height * 0.61);
    ctx.lineTo(x + width * 0.02, height * 0.94);
    ctx.lineTo(x - width * 0.03, height * 0.94);
    ctx.closePath();
    ctx.fill();
  }

  ctx.strokeStyle = `rgba(241,245,249,${0.9 + goalPulse * 0.08})`;
  ctx.lineWidth = 2.2;
  drawPitchLines(ctx, width, height, [{ x: -18.5, y: 0.01, z: 5.8 }, { x: 18.5, y: 0.01, z: 5.8 }, { x: 18.5, y: 0.01, z: 23.8 }, { x: -18.5, y: 0.01, z: 23.8 }]);
  drawPitchLines(ctx, width, height, [{ x: -6.4, y: 0.01, z: 15.8 }, { x: 6.4, y: 0.01, z: 15.8 }, { x: 6.4, y: 0.01, z: 22.3 }, { x: -6.4, y: 0.01, z: 22.3 }]);
  drawPitchLines(ctx, width, height, [{ x: -2.45, y: 0.01, z: 11.28 }, { x: 2.45, y: 0.01, z: 11.28 }, { x: 2.45, y: 0.01, z: 11.9 }, { x: -2.45, y: 0.01, z: 11.9 }]);

  const spot = projectPoint({ x: 0, y: 0.02, z: 11.8 }, width, height);
  ctx.beginPath();
  ctx.fillStyle = 'rgba(248,250,252,0.95)';
  ctx.arc(spot.x, spot.y, Math.max(2.2, spot.scale * 0.045), 0, Math.PI * 2);
  ctx.fill();

  ctx.beginPath();
  ctx.strokeStyle = 'rgba(226,232,240,0.62)';
  ctx.lineWidth = 1.6;
  for (let angle = 0; angle <= Math.PI; angle += Math.PI / 36) {
    const arcPoint = projectPoint({ x: Math.cos(angle) * 2.9, y: 0.01, z: 14.25 + Math.sin(angle) * 1.1 }, width, height);
    if (angle === 0) ctx.moveTo(arcPoint.x, arcPoint.y);
    else ctx.lineTo(arcPoint.x, arcPoint.y);
  }
  ctx.stroke();
};

export const drawGoal = (ctx: CanvasRenderingContext2D, width: number, height: number, goalPulse: number) => {
  const front = {
    left: { x: -GOAL_FRAME.halfWidth, y: 0, z: GOAL_FRAME.frontZ },
    right: { x: GOAL_FRAME.halfWidth, y: 0, z: GOAL_FRAME.frontZ },
    leftTop: { x: -GOAL_FRAME.halfWidth, y: GOAL_FRAME.crossbarY, z: GOAL_FRAME.frontZ },
    rightTop: { x: GOAL_FRAME.halfWidth, y: GOAL_FRAME.crossbarY, z: GOAL_FRAME.frontZ },
  };
  const back = {
    left: { x: -GOAL_FRAME.halfWidth, y: 0, z: GOAL_FRAME.backZ },
    right: { x: GOAL_FRAME.halfWidth, y: 0, z: GOAL_FRAME.backZ },
    leftTop: { x: -GOAL_FRAME.halfWidth, y: GOAL_FRAME.crossbarY, z: GOAL_FRAME.backZ },
    rightTop: { x: GOAL_FRAME.halfWidth, y: GOAL_FRAME.crossbarY, z: GOAL_FRAME.backZ },
  };

  ctx.strokeStyle = `rgba(191,219,254,${0.18 + goalPulse * 0.12})`;
  ctx.lineWidth = 1.2;
  for (let index = 0; index <= 6; index += 1) {
    const depth = GOAL_FRAME.frontZ + ((GOAL_FRAME.backZ - GOAL_FRAME.frontZ) * index) / 6;
    const left = projectPoint({ x: -GOAL_FRAME.halfWidth, y: 0, z: depth }, width, height);
    const leftTop = projectPoint({ x: -GOAL_FRAME.halfWidth, y: GOAL_FRAME.crossbarY, z: depth }, width, height);
    const right = projectPoint({ x: GOAL_FRAME.halfWidth, y: 0, z: depth }, width, height);
    const rightTop = projectPoint({ x: GOAL_FRAME.halfWidth, y: GOAL_FRAME.crossbarY, z: depth }, width, height);
    ctx.beginPath();
    ctx.moveTo(left.x, left.y);
    ctx.lineTo(leftTop.x, leftTop.y);
    ctx.moveTo(right.x, right.y);
    ctx.lineTo(rightTop.x, rightTop.y);
    ctx.stroke();
  }

  for (let index = 0; index <= 8; index += 1) {
    const y = (GOAL_FRAME.crossbarY * index) / 8;
    const left = projectPoint({ x: -GOAL_FRAME.halfWidth, y, z: GOAL_FRAME.backZ }, width, height);
    const right = projectPoint({ x: GOAL_FRAME.halfWidth, y, z: GOAL_FRAME.backZ }, width, height);
    ctx.beginPath();
    ctx.moveTo(left.x, left.y);
    ctx.lineTo(right.x, right.y);
    ctx.stroke();
  }

  const frameEdges = [
    [front.left, front.leftTop], [front.right, front.rightTop], [front.leftTop, front.rightTop], [front.left, back.left],
    [front.right, back.right], [front.leftTop, back.leftTop], [front.rightTop, back.rightTop], [back.leftTop, back.rightTop],
  ] as const;
  ctx.strokeStyle = `rgba(248,250,252,${0.98 - goalPulse * 0.04})`;
  ctx.lineWidth = 4.2 + goalPulse * 0.6;
  ctx.lineCap = 'round';
  frameEdges.forEach(([start, end]) => {
    const a = projectPoint(start, width, height);
    const b = projectPoint(end, width, height);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  });

  const mouthGlow = ctx.createRadialGradient(width * 0.5, height * 0.55, 14, width * 0.5, height * 0.55, width * 0.2);
  mouthGlow.addColorStop(0, `rgba(248,250,252,${0.08 + goalPulse * 0.07})`);
  mouthGlow.addColorStop(1, 'rgba(248,250,252,0)');
  ctx.fillStyle = mouthGlow;
  ctx.fillRect(0, height * 0.45, width, height * 0.3);

  const netShadow = ctx.createLinearGradient(0, height * 0.61, 0, height * 0.78);
  netShadow.addColorStop(0, 'rgba(15,23,42,0)');
  netShadow.addColorStop(1, 'rgba(15,23,42,0.22)');
  ctx.fillStyle = netShadow;
  ctx.beginPath();
  const shadowLeft = projectPoint({ x: -6.2, y: 0.01, z: 21.2 }, width, height);
  const shadowRight = projectPoint({ x: 6.2, y: 0.01, z: 21.2 }, width, height);
  const shadowFarRight = projectPoint({ x: 8.2, y: 0.01, z: 24.2 }, width, height);
  const shadowFarLeft = projectPoint({ x: -8.2, y: 0.01, z: 24.2 }, width, height);
  ctx.moveTo(shadowLeft.x, shadowLeft.y);
  ctx.lineTo(shadowRight.x, shadowRight.y);
  ctx.lineTo(shadowFarRight.x, shadowFarRight.y);
  ctx.lineTo(shadowFarLeft.x, shadowFarLeft.y);
  ctx.closePath();
  ctx.fill();
};

export const drawSceneBackground = (ctx: CanvasRenderingContext2D, width: number, height: number, goalPulse: number) => {
  drawBackdrop(ctx, width, height, goalPulse);
  drawPitch(ctx, width, height, goalPulse);
  drawGoal(ctx, width, height, goalPulse);
};
